/**
 * FounderDashboard — super-admin–only view inside the Admin/Operations console.
 *
 * Visibility:
 *   Rendered as the first tab ("founder") when user.is_super_admin === true
 *   OR user.email === "travis.mcgruder@220inc.com". Hidden for everyone else.
 *
 * Transparency principle:
 *   No fake numbers. Every metric is presented as a status label aligned
 *   with the platform-wide forward-looking statement.
 */
import { Crown, Building2, Layers, Users, DollarSign, Sparkles, Home as HomeIcon } from "lucide-react";
import { GlassCard } from "../../components/site/Primitives";

const FOUNDER_CARDS = [
  { key: "properties",        label: "Total Properties",          status: "Planned",                  icon: <Building2 size={20} />,    tone: "text-gold" },
  { key: "units",             label: "Total Units",               status: "Planned",                  icon: <Layers size={20} />,       tone: "text-gold" },
  { key: "investors",         label: "Total Investors",           status: "Soft-Circling",            icon: <Users size={20} />,        tone: "text-gold" },
  { key: "capital",           label: "Total Capital Raised",      status: "Not Publicly Disclosed",   icon: <DollarSign size={20} />,   tone: "text-white/80" },
  { key: "active_projects",   label: "Active Projects",           status: "In Planning",              icon: <Sparkles size={20} />,     tone: "text-gold" },
  { key: "vh_occupancy",      label: "Veteran Housing Occupancy", status: "Not Operating Yet",        icon: <HomeIcon size={20} />,     tone: "text-white/80" },
];

export default function FounderDashboard({ user }) {
  return (
    <div data-testid="founder-dashboard">
      {/* Header row with gold pill */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div className="flex items-center gap-3">
          <Crown size={22} className="text-gold" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">Private View</div>
            <div className="font-display text-2xl mt-0.5">Founder Dashboard</div>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-black bg-gradient-to-r from-[#FFEB99] via-[#E6B800] to-[#A88300] px-3.5 py-1.5 font-semibold rounded-full"
          data-testid="founder-pill"
        >
          <Crown size={12} /> Founder / CEO
        </span>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {FOUNDER_CARDS.map((c) => (
          <div key={c.key} className="bg-[#0c0c0e] p-6 flex flex-col" data-testid={`founder-card-${c.key}`}>
            <div className={`flex items-center gap-2 ${c.tone}`}>
              {c.icon}
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/45">{c.label}</span>
            </div>
            <div className="mt-5">
              <span className="inline-flex items-center text-xs uppercase tracking-[0.2em] text-gold border border-gold/30 px-2.5 py-1 bg-gold/5">
                {c.status}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Transparency footnote — same language family as the site-wide disclosure */}
      <div className="mt-8 border border-white/10 bg-[#0c0c0e] p-5 text-[11px] text-white/50 leading-relaxed">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">Founder Notes</div>
        Signed in as <span className="text-white/85">{user?.name || "Travis R. McGruder"}</span> · <span className="text-white/85">{user?.email}</span>.
        Figures shown are forward-looking — no assets have been acquired, no facilities are operating, and no capital has been disclosed publicly.
        This view is private to the Founder / CEO and is not exposed on the public website.
      </div>
    </div>
  );
}
